import { createFileRoute } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { Award, Users, Heart, Target, Sparkles, HandHeart } from "lucide-react";
import heroImg from "@/assets/hero-epoxy.jpg";
import thomasPhoto from "@/assets/thomas-zamora.webp.asset.json";
import { IdCard } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About | Blanco Pro Services, New Jersey" },
      { name: "description", content: "Family-rooted epoxy flooring and property improvement company serving New Jersey, Pennsylvania, and New York. Built on faith, integrity, and craftsmanship." },
      { property: "og:title", content: "About Blanco Pro Services" },
      { property: "og:description", content: "Family-rooted craft. Built in New Jersey, serving the tri-state area." },
      { property: "og:url", content: "https://blancopros.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Blanco Pro Services" },
      { name: "twitter:description", content: "Family-rooted craft. Built in New Jersey, serving the tri-state area." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Thomas Blanco",
          jobTitle: "Founder & Owner",
          worksFor: { "@id": "https://blancopros.com/#organization" },
          description: "Founder of Blanco Pro Services, currently pursuing a degree in Business Administration. Brings hands-on experience in hardscaping, concrete, and two years of residential junk removal and demolition.",
          address: {
            "@type": "PostalAddress",
            addressRegion: "NJ",
            addressCountry: "US",
          },
          knowsAbout: ["Epoxy Flooring", "Hardscaping", "Concrete", "Demolition", "Property Improvement"],
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      {/* HERO */}
      <section className="pt-40 pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-hero relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-96 h-96 rounded-full bg-gradient-gold blur-3xl opacity-20 animate-float" />
        <div className="relative max-w-5xl mx-auto animate-fade-up">
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">About Us</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.95] mb-8 max-w-4xl">
            Craft is in the <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">details</em>. So is our reputation.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            Blanco Pro Services is a New Jersey–based property improvement company specializing in premium epoxy flooring, hardscaping, and demolition. The Blanco name is rooted in family heritage, a legacy of honor, integrity, and respect, and that's the standard we bring to every floor we pour and every client we serve across New Jersey, Pennsylvania, and New York.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="rounded-3xl overflow-hidden aspect-[21/9] mb-20 shadow-elegant">
              <img src={heroImg} alt="Premium epoxy floor finish" loading="lazy" width={1920} height={1280} className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-16 mb-24 items-start">
            <Reveal>
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Our Mission</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight">Improving lives from the ground up.</h2>
            </Reveal>
            <Reveal delay={150}>
              <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                <p>Blanco Pro Services exists to elevate the living and working conditions of the people we serve, one floor at a time. We deliver durable, high-quality flooring solutions that improve the spaces where people live, work, and build their lives.</p>
                <p>Whether it's a homeowner finally finishing a basement, a barbershop opening its second location, or a dental office rebuilding after a renovation, we treat every job as a foundation for something bigger than a floor.</p>
              </div>
            </Reveal>
          </div>

          {/* CORE VALUES */}
          <Reveal>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Core Values</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold">What we stand on.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mb-24">
            {[
              { icon: Award, t: "The Standard", d: "Every project is a direct reflection of our name. We never cut corners, our work has to hold up to the Blanco standard before it leaves the site." },
              { icon: Sparkles, t: "Built on Faith", d: "This business was started in faith. We operate with purpose and intentionality, holding ourselves to an excellence that goes beyond profit." },
              { icon: HandHeart, t: "Blessing Others", d: "Quality work is an act of service. A strong, well-crafted floor is more than cosmetic, it's a foundation that improves someone's everyday life." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 120}>
                <div className="card-tilt p-8 rounded-2xl border border-border bg-card h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-gold flex items-center justify-center mb-5 shadow-gold">
                    <v.icon className="w-5 h-5 text-gold-foreground" />
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3">{v.t}</h3>
                  <p className="text-muted-foreground leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* FOUNDER */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <Reveal>
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Founder</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight">Meet Thomas.</h2>
              <div className="flex items-center gap-5 mb-6">
                <img
                  src={thomasPhoto.url}
                  alt="Thomas Zamora, Founder of Blanco Pro Services"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-gold shadow-gold shrink-0"
                />
                <a
                  href="https://thomas.blancopros.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-gold text-gold-foreground font-semibold text-sm shadow-gold hover:brightness-110 transition"
                >
                  <IdCard className="w-4 h-4" />
                  Digital Business Card
                </a>
              </div>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Blanco Pro Services was founded by Thomas, a 20-year-old entrepreneur currently pursuing a degree in Business Administration. Raised with a strong work ethic, he developed hands-on experience in hardscaping and concrete from an early age, and brings two years of professional experience in residential junk removal and demolition.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Blanco was built on his genuine desire to serve people and create something lasting. When you call, you get straight answers and a real person, not a script.
              </p>
            </Reveal>
            <Reveal delay={150}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Award, t: "Quality First", d: "Premium materials and finishes built to last." },
                  { icon: Heart, t: "Local Roots", d: "Based across New Jersey. We live here too." },
                  { icon: Users, t: "Customer Obsessed", d: "Clear communication from first call to final coat." },
                  { icon: Target, t: "Precision Work", d: "Detailed prep on every single job." },
                ].map((v) => (
                  <div key={v.t} className="card-tilt p-6 rounded-2xl border border-border bg-card">
                    <div className="w-10 h-10 rounded-xl bg-gradient-gold flex items-center justify-center mb-4 shadow-gold">
                      <v.icon className="w-4 h-4 text-gold-foreground" />
                    </div>
                    <h3 className="font-display text-lg font-bold mb-1">{v.t}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{v.d}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-secondary">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Who We Serve</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Built for NJ's best.</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-12">
              Barbershops, hair salons, gyms, dental offices, detailing & tint shops, and homeowners upgrading garages, basements, and interiors.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {["Homeowners", "Barbershops", "Hair Salons", "Gyms", "Dental Offices", "Detailing Shops"].map((g, i) => (
              <Reveal key={g} delay={i * 80}>
                <div className="aspect-square rounded-2xl bg-background border border-border flex flex-col items-center justify-center p-2 card-tilt">
                  <div className="font-display text-3xl font-bold bg-gradient-gold bg-clip-text text-transparent mb-1">0{i+1}</div>
                  <div className="font-semibold text-xs text-center px-1">{g}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
